import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';

const CharacterBasicInfo = ({ character, onSave, isNew = false }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        char_id: '',
        name_en: '',
        name_ja: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (character) {
            setFormData({
                char_id: character.char_id || '',
                name_en: character.name_en || '',
                name_ja: character.name_ja || ''
            });
        }
    }, [character]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear success message on change
        if (success) setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // If it's part of a larger form (new character), just pass data up
            if (isNew) {
                onSave(formData);
                setLoading(false);
                return;
            }

            // Otherwise save directly
            const response = await fetch(`${API_BASE_URL}/characters/${character.Id_Personaje}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || t('CharacterBasicInfo.error_update'));
            }

            const data = await response.json();
            setSuccess(true);
            if (onSave) onSave(data.character);

            // Hide success message after 3 seconds
            setTimeout(() => setSuccess(false), 3000);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-800">{t('CharacterBasicInfo.title')}</h3>
                {!isNew && character && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {t('CharacterBasicInfo.label_internal_id')}: {character.Id_Personaje}
                    </span>
                )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Character ID (Game ID) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterBasicInfo.label_game_id')}
                        </label>
                        <input
                            type="number"
                            name="char_id"
                            value={formData.char_id}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent"
                            required
                            placeholder="e.g. 1001"
                        />
                    </div>

                    {/* English Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterBasicInfo.label_name_en')}
                        </label>
                        <input
                            type="text"
                            name="name_en"
                            value={formData.name_en}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent"
                            required
                            placeholder="e.g. Special Week"
                        />
                    </div>

                    {/* Japanese Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterBasicInfo.label_name_ja')}
                        </label>
                        <input
                            type="text"
                            name="name_ja"
                            value={formData.name_ja}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent"
                            required
                            placeholder="e.g. スペシャルウィーク"
                        />
                    </div>
                </div>

                {/* Dates Info (Read Only) */}
                {!isNew && character && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                        <div>
                            <span className="text-xs text-gray-500 block">{t('CharacterBasicInfo.label_created_at')}</span>
                            <span className="text-sm text-gray-700">
                                {character.created_at ? new Date(character.created_at).toLocaleString() : '-'}
                            </span>
                        </div>
                        <div>
                            <span className="text-xs text-gray-500 block">{t('CharacterBasicInfo.label_updated_at')}</span>
                            <span className="text-sm text-gray-700">
                                {character.updated_at ? new Date(character.updated_at).toLocaleString() : '-'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Error/Success Messages */}
                {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                        {t('CharacterBasicInfo.success_msg')}
                    </div>
                )}

                {/* Save Button (Only show if not new, or if handled by parent) */}
                {!isNew && (
                    <div className="flex justify-end pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 bg-primary-green text-white rounded-lg font-medium hover:bg-green-600 transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? t('CharacterBasicInfo.button_saving') : t('CharacterBasicInfo.button_save')}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CharacterBasicInfo;
// Revisado: 2025-12-16
